import { Avatar, Spinner } from "@heroui/react";
import { z } from "zod";
import { CustomModal, StaticSelect, TextInput, FormError, Button, showToast } from "../shared";
import { useInviteTeamMember } from "../../lib/react-query/teams";
import { useNetworkFilterSearch } from "../../lib/react-query/partners.ts";
import { useTeamInviteStore } from "../../stores/teamInviteStore";

const ROLE_OPTIONS = [
  { id: "ADMIN", name: "Admin" },
  { id: "MEMBER", name: "Member" },
];

const ROLE_DESCRIPTIONS = {
  ADMIN: "Can manage members, invitations, settings, and shared files.",
  MEMBER: "Can work with projects and files allowed by team access.",
};

const PROJECT_ROLE_OPTIONS = [
  { id: "COLLABORATOR", name: "Collaborator" },
  { id: "MANAGER", name: "Manager" },
];

const inviteSchema = z.object({
  email: z.string().trim().min(1, "Choose a connection or enter an email address.").email("Please enter a valid email address."),
  role: z.enum(["ADMIN", "MEMBER"]),
  projectRole: z.enum(["MANAGER", "COLLABORATOR"]),
  allProjectsAccess: z.boolean(),
  projectIds: z.array(z.number().int().positive()),
}).superRefine((data, context) => {
  if (!data.allProjectsAccess && data.projectIds.length === 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["projectIds"],
      message: "Select at least one project.",
    });
  }
});

const InviteTeamMemberModal = ({ isOpen, onClose, teamId, projects = [] }) => {
  const { mutateAsync: inviteMember, isLoading } = useInviteTeamMember();
  const {
    recipient,
    selectedRecipient,
    search,
    role,
    projectRole,
    allProjectsAccess,
    projectIds,
    error,
    setRecipient,
    selectRecipient,
    clearRecipient,
    setRole,
    setProjectRole,
    setAllProjectsAccess,
    toggleProject,
    setError,
    reset,
  } = useTeamInviteStore();

  const { data: connectionResults = [], isLoading: connectionsLoading } = useNetworkFilterSearch(search, isOpen);
  const selectableProjects = projects.filter((project) => project.visibility !== "RESTRICTED");

  const recipientName = (item) =>
    item?.full_name || [item?.first_name, item?.last_name].filter(Boolean).join(" ") || item?.email || "Connection";

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleInvite = async () => {
    try {
      const email = selectedRecipient?.email || recipient.trim();
      const parsedInvite = inviteSchema.safeParse({
        email,
        role,
        projectRole,
        allProjectsAccess,
        projectIds,
      });
      if (!parsedInvite.success) {
        setError(parsedInvite.error.issues[0]?.message || "Please check the invite details.");
        return;
      }
      setError(null);
      await inviteMember({
        teamId,
        payload: {
          email: parsedInvite.data.email,
          role: parsedInvite.data.role,
          project_role: parsedInvite.data.projectRole,
          all_projects_access: parsedInvite.data.allProjectsAccess,
          project_ids: parsedInvite.data.allProjectsAccess ? [] : parsedInvite.data.projectIds,
        },
      });
      showToast("Invitation sent successfully", "success", { position: "top-center" });
      handleClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to send invitation.";
      setError(msg);
    }
  };

  return (
    <CustomModal
      title="Invite team member"
      open={isOpen}
      close={handleClose}
      primaryButtonText="Send invite"
      primaryButtonClick={handleInvite}
      primaryButtonLoading={isLoading}
      secondaryButtonText="Cancel"
      secondaryButtonClick={handleClose}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-6 text-grey">
          Search a connection or invite anyone by email. You can adjust access after they join.
        </p>
        <div className="relative">
          <TextInput
            label="Recipient"
            name="recipient"
            type="text"
            value={selectedRecipient ? recipientName(selectedRecipient) : recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="Search connections or enter an email..."
            disabled={!!selectedRecipient}
            required
          />
          {!selectedRecipient && recipient.trim().length >= 2 && (
            <div className="absolute left-0 right-0 z-50 mt-2 max-h-52 overflow-auto rounded-lg border bg-white shadow">
              {connectionsLoading ? (
                <div className="flex justify-center py-5"><Spinner size="sm" /></div>
              ) : connectionResults.length > 0 ? (
                connectionResults.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface"
                    onClick={() => {
                      selectRecipient(item);
                    }}
                  >
                    <Avatar src={item.profile_image || undefined} className="!size-7" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{recipientName(item)}</span>
                      <span className="block truncate text-xs text-mediumgrey">{item.email || "Connection"}</span>
                    </span>
                  </button>
                ))
              ) : (
                <p className="px-3 py-2 text-sm text-mediumgrey">
                  Invite directly by email if there is no connection match.
                </p>
              )}
            </div>
          )}
          {selectedRecipient && (
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-mediumgrey">Selected connection</span>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                size="sm"
                onClick={() => {
                  clearRecipient();
                }}
              >
                Change
              </Button>
            </div>
          )}
        </div>
        <StaticSelect
          label="Role"
          name="role"
          options={ROLE_OPTIONS}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />
        <p className="-mt-2 text-xs text-mediumgrey" aria-live="polite">
          {ROLE_DESCRIPTIONS[role]}
        </p>
        <StaticSelect
          label="Project role"
          name="projectRole"
          options={PROJECT_ROLE_OPTIONS}
          value={projectRole}
          onChange={(e) => setProjectRole(e.target.value)}
        />
        <StaticSelect
          label="Project access"
          name="projectAccess"
          options={[
            { id: "ALL", name: "All projects" },
            { id: "SELECTED", name: "Selected projects" },
          ]}
          value={allProjectsAccess ? "ALL" : "SELECTED"}
          onChange={(e) => setAllProjectsAccess(e.target.value === "ALL")}
        />
        {!allProjectsAccess && (
          <div className="rounded-lg border border-surface-border p-3">
            <p className="mb-2 text-sm font-medium text-darkgrey">Choose projects</p>
            <div className="space-y-2">
              {selectableProjects.length > 0 ? selectableProjects.map((project) => (
                <label key={project.id} className="flex items-center gap-2 text-sm text-darkgrey">
                  <input
                    type="checkbox"
                    checked={projectIds.includes(project.id)}
                    onChange={() => toggleProject(project.id)}
                  />
                  <span>{project.title || project.name || "Untitled project"}</span>
                </label>
              )) : (
                <p className="text-sm text-mediumgrey">No team projects are available.</p>
              )}
            </div>
          </div>
        )}
        <FormError message={error} />
        <p className="text-xs text-grey mt-1">
          Team invites are for internal collaborators. Client Contacts should be invited from the project.
        </p>
      </div>
    </CustomModal>
  );
};

export default InviteTeamMemberModal;
